import React from 'react';
import {Caption1, Card, CardHeader, CardPreview, Text, Button, Spinner, Dialog, DialogSurface, DialogTitle, DialogBody, DialogContent, DialogActions, Tooltip} from '@fluentui/react-components';
import useStyles from '../../styles';
import { useNavigate } from 'react-router-dom';
import { IStorefrontBundle } from '../../Api/Models/Products';
import CurrencyFormat from '../Helpers/CurrencyFormat';
import TruncateText from '../Helpers/TruncateText';
import { ProductImage } from "../Helpers/ProductImage";
import {CartRegular, NewFilled, ProhibitedRegular} from "@fluentui/react-icons";
import {useAddBundleToCartMutation} from "../../Api/Api";
import {useAddBundleToPreparedCartMutation, useGetPreparedCartQuery} from "../../Api/PreparedCartEndpoints";
import {useSelector} from "react-redux";
import {RootState} from "../../Store/Store";
import {IActionResponse} from "../../Api/Models/ActionResponse";
import CAN, {CART_MANAGEMENT} from "../Auth/Permissions";
import {CART_DRAG_MIME} from "../Cart/useAddToCartAction";
import {useStartNewCart} from "../Cart/useStartNewCart";
import {useLocalization} from "../Helpers/useLocalization";
import {buildDefaultBundleSelection} from "../Bundle/useBundleConfiguration";

interface IBundleCardProps {
    bundle: IStorefrontBundle;
    showAddToCart?: boolean;
}

const BundleCard: React.FC<IBundleCardProps> = (props) => {
    const L = useLocalization();
    const settings = useSelector((state: RootState) => state.cart);
    const { startNewCart, canStartNewCart } = useStartNewCart();
    const styles = useStyles();
    const nav = useNavigate();
    const [added, setAdded] = React.useState(false);
    const [addBundleToCart, { isLoading: isBundleLoading }] = useAddBundleToCartMutation();
    const [addBundleToPreparedCart, { isLoading: isPreparedCartBundleLoading }] = useAddBundleToPreparedCartMutation();
    const getPreparedCart = useGetPreparedCartQuery({cartId: settings.selectedCartId ?? ''}, {skip: !settings.selectedCartId});
    const [blockedDialogOpen, setBlockedDialogOpen] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const cartAddPermission = CAN(CART_MANAGEMENT);


    // Derive directly — no intermediate state, no render lag
    const hasPreparedCart = !!settings.selectedCartId && !!getPreparedCart.data;
    const preparedCartAllowsAdd = hasPreparedCart && (getPreparedCart.data?.canAddAdditionalItems ?? false);
    const canAddToCart = (props.showAddToCart ?? false) && (!hasPreparedCart || preparedCartAllowsAdd) && cartAddPermission;
    const cartBlocked = (props.showAddToCart ?? false) && hasPreparedCart && !preparedCartAllowsAdd;

    const showError = (msg: string) => {
        setErrorMessage(msg);
        setTimeout(() => setErrorMessage(''), 5000);
    };

    const checkMessages = (cart?: { messages?: string[] }): boolean => {
        if (cart?.messages && cart.messages.length > 0) {
            showError(cart.messages.join(' '));
            return true;
        }
        return false;
    };

    const checkError = (result: { error?: unknown }): boolean => {
        if (result.error) {
            const actionResponse = (result.error as any)?.data as IActionResponse | undefined;
            showError(actionResponse?.error ?? actionResponse?.message ?? 'An error occurred. Please try again.');
            return true;
        }
        return false;
    };

    const click = () => {
        nav(`/bundles/${props.bundle.id}`);
    }

    return (
        <Card
            className='card'
            onClick={click}
            style={{cursor:'pointer', backgroundColor: 'white', position: 'relative'}}
            // Drag-to-cart: dropping a bundle card on the header cart icon adds the bundle.
            draggable={canAddToCart}
            onDragStart={canAddToCart ? (e) => {
                // BundleCard is only ever rendered in the home page's featured-bundles section today.
                e.dataTransfer.setData(CART_DRAG_MIME, JSON.stringify({ bundleId: props.bundle.id, bundleSelection: buildDefaultBundleSelection(props.bundle), source: 'section' }));
                e.dataTransfer.effectAllowed = 'copy';
            } : undefined}
        >
            {/* No onClick here: the parent Card's onClick already fires for clicks anywhere in the
                card (they bubble up). A second handler here double-fired nav(), pushing a duplicate
                history entry so Back appeared to do nothing on the first press. */}
            <CardPreview
                className={styles.grayBackground}
                style={{cursor:'pointer', height: '200px', width: '100%', paddingTop: '10px', margin: '0 auto', backgroundColor: 'white'}}
                logo={<BundleFlag />}
            >
                <div style={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <ProductImage
                        className={styles.smallRadius}
                        src={props.bundle.image ?? undefined}
                        alt="Presentation Preview"
                        fit='contain'
                    />
                </div>
            </CardPreview>

            <CardHeader
                header={<Text weight="semibold"><TruncateText maxLength={50} text={props.bundle.name ?? ''} truncateOnWord /></Text>}
                description={
                    <>
                        {props.bundle.price && <Caption1 className={styles.caption}><CurrencyFormat value={props.bundle.price} /></Caption1>}
                    </>
                }
            />
            {/* Floating Add to Cart Button */}
            {canAddToCart && (
                <Button
                    appearance="primary"
                    size="medium"
                    icon={isBundleLoading || isPreparedCartBundleLoading ? <Spinner size="tiny" /> : <CartRegular />}
                    aria-label="Add to cart"
                    style={{
                        position: 'absolute',
                        right: 12,
                        bottom: 12,
                        zIndex: 2,
                        borderRadius: '50%',
                        minWidth: 0,
                        height: 30,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                    }}
                    disabled={isBundleLoading || isPreparedCartBundleLoading}
                    onClick={async (e) => {
                        e.stopPropagation();
                        // Read fresh values at click time, not from stale state
                        const activeCartId = settings.selectedCartId;
                        const usePreparedCart = !!activeCartId && (getPreparedCart.data?.canAddAdditionalItems ?? false);
                        let success = false;
                        setErrorMessage('');
                        try {
                            // Send the bundle's default selection so the backend records its line items
                            // (a fully-required bundle otherwise adds with no per-item selection and its
                            // items never appear in the cart).
                            const selection = buildDefaultBundleSelection(props.bundle);
                            if (usePreparedCart) {
                                const result = await addBundleToPreparedCart({cartId: activeCartId!, bundleId: props.bundle.id, selection});
                                if (!checkError(result) && !checkMessages(result.data?.cart)) success = true;
                            } else {
                                // BundleCard is only ever rendered in the home page's featured-bundles section today.
                                const result = await addBundleToCart({bundleId: props.bundle.id, selection, source: 'section'});
                                if (!checkError(result) && !checkMessages((result as any).data)) success = true;
                            }
                        } catch {}
                        if (success) {
                            setAdded(true);
                            setTimeout(() => setAdded(false), 1500);
                        }
                    }}
                />
            )}
            {/* Blocked Cart Button */}
            {cartBlocked && (
                <Tooltip content="Cannot add to current Prepared Cart" relationship="label">
                    <Button
                        appearance="subtle"
                        size="medium"
                        icon={<ProhibitedRegular style={{ color: '#d13438' }} />}
                        aria-label="Cannot add to cart"
                        style={{
                            position: 'absolute',
                            right: 12,
                            bottom: 12,
                            zIndex: 2,
                            borderRadius: '50%',
                            minWidth: 0,
                            height: 30,
                        }}
                        onClick={(e) => { e.stopPropagation(); setBlockedDialogOpen(true); }}
                    />
                </Tooltip>
            )}
            <Dialog open={blockedDialogOpen} onOpenChange={(_, d) => setBlockedDialogOpen(d.open)}>
                <DialogSurface onClick={e => e.stopPropagation()}>
                    <DialogBody>
                        <DialogTitle>Cannot Add to Cart</DialogTitle>
                        <DialogContent>
                            Your current Prepared Cart does not allow adding additional items.{canStartNewCart ? ' You can view your current cart or start a new one.' : ''}
                        </DialogContent>
                        <DialogActions>
                            <Button appearance="primary" onClick={() => { setBlockedDialogOpen(false); nav(`/prepared-cart/${settings.selectedCartId}`); }}>
                                {L('localization_common_go_to_cart')}
                            </Button>
                            {canStartNewCart &&
                            <Button appearance="secondary" icon={<NewFilled/>} onClick={async () => { setBlockedDialogOpen(false); await startNewCart(); nav('/cart'); }}>
                                New Cart
                            </Button>}
                            <Button appearance="transparent" onClick={() => setBlockedDialogOpen(false)}>{L('localization_common_cancel')}</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
            {added && (
                <span
                    style={{
                        position: 'absolute',
                        right: 60,
                        bottom: 20,
                        background: 'rgba(255,255,255,0.95)',
                        color: '#00744d',
                        fontWeight: 600,
                        fontSize: 14,
                        borderRadius: 6,
                        padding: '2px 10px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        pointerEvents: 'none',
                        transition: 'opacity 0.2s',
                    }}
                >
                    {L('localization_common_added')}
                </span>
            )}
            {errorMessage && (
                <span
                    style={{
                        position: 'absolute',
                        left: 8,
                        right: 50,
                        bottom: 8,
                        background: 'rgba(255,255,255,0.97)',
                        color: '#d13438',
                        fontWeight: 600,
                        fontSize: 12,
                        borderRadius: 6,
                        padding: '4px 8px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                        pointerEvents: 'none',
                        zIndex: 3,
                    }}
                    onClick={e => { e.stopPropagation(); setErrorMessage(''); }}
                >
                    {errorMessage}
                </span>
            )}
        </Card>
    )
}

const BundleFlag: React.FC = () => {
    const styles = useStyles();
    return (
        <div className={styles.featuredFlag}>
            <Text weight="semibold" className={styles.featuredText}>BUNDLE</Text>
        </div>
    )
}

export default BundleCard;